import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { Button, Icon, Badge } from '@pwc/pwc-deals-ui';

import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

import featureFlags from 'common/helpers/featureFlags';
import useGlobalError from 'common/components/GlobalError/useGlobalError';
import ActionsWithLock from 'common/components/ActionsWithLock/ActionsWithLock';
import VersionToggleContainer from 'common/components/VersionToggle/VersionToggleContainer';
import useLock from 'common/hooks/useLock';
import {
  getCommentPanelOpened,
  getViewingStatus,
  setCommentPanelOpened,
  viewUnresolvedComments,
  getRibbonPanelItem,
} from 'features/comments/commentsSlice';
import { useCommentCounts } from 'common/hooks/useCommentCounts';
import { useMobileContentHighlight } from 'common/hooks/useMobileContentHighlight';

import './view-hypothesis.scss';
import analysisStyles from 'features/analyses/common/AnalysisControlsHeader.module.scss';
import useMobile from '../../../common/hooks/useMobile';
import styles from './ViewHypothesis.module.scss';

import { COMMENT_VIEWING_STATUS } from '../../comments/constants';
import { getIsDarkModeSelector } from '../../../common/redux/userSettings/userSettingsSlice';

const ViewHypothesisHeader = ({
  config,
  disableEdit = false,
  disableMoreMenu = false,
  disableVersionToggleButtons,
  gotoEditHypothesis,
  hideEdit,
  hideSelectedVersionToggleButtons = false,
  hideToggleVersion = false,
  isEntitySharedWithClient,
  kfa,
  kfaContent,
  projectId,
  reportPreviewUrl,
  rightAlignItemsToright = false,
  showWithoutContent = false,
  setShowDeleteConfirmation,
  viewClient,
  workflowActions,
  commentingAutoLockContentId,
}) => {
  const isMobile = useMobile();
  const dispatch = useDispatch();
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const viewingStatus = useSelector(getViewingStatus);
  const ribbonPanelItemType = useSelector(getRibbonPanelItem);

  const isPanelOpened = useMemo(
    () => ribbonPanelItemType.type != null,
    [ribbonPanelItemType.type]
  );
  const { visibleCommentCounts } = useCommentCounts({
    isShared: isEntitySharedWithClient,
    viewingStatus,
    isPanelOpened,
  });

  const visibleCommentCountsText =
    visibleCommentCounts > 9 ? '9+' : visibleCommentCounts;
  const commentPanelOpened = useSelector(getCommentPanelOpened);
  const darkMode = useSelector(getIsDarkModeSelector);
  const { name } = useLock({ contentThreadId: kfa?.content?.contentThreadId });

  useMobileContentHighlight({ commentPanelOpened, isMobile });

  useGlobalError({
    containerId: '.global-error-placeholder',
    metadata: {
      bannerMetadata: { alertLevel: 'info', bannerPosition: 'bottom' },
    },
    userName: name,
  });

  const handlePanelButtonClick = useCallback(() => {
    if (viewingStatus !== COMMENT_VIEWING_STATUS.UNRESOLVED) {
      dispatch(viewUnresolvedComments());
    }
    dispatch(setCommentPanelOpened(!commentPanelOpened));
  }, [commentPanelOpened, dispatch, viewingStatus]);

  return (
    <>
      {(kfaContent || showWithoutContent) && (
        <>
          <div
            className={classnames(
              styles['hypothesis-top-bar-container'],
              !isPwcUser
                ? styles['current-user-is-client']
                : styles['current-user-is-pwc']
            )}
          >
            <div
              className={classnames(styles['hypothesis-top-bar'], {
                [styles.rightAlignFlexItemsToright]: rightAlignItemsToright,
              })}
            >
              {!hideToggleVersion && (
                <div
                  className={classnames(
                    styles['kfa-header'],
                    'global-error-placeholder'
                  )}
                >
                  <VersionToggleContainer
                    redirectUrl={`/projects/${projectId}/key-focus-areas`}
                    config={config}
                    disableVersionToggleButtons={disableVersionToggleButtons}
                    hideTextVersion={featureFlags.isVersioningPhase1Enabled}
                    isEntitySharedWithClient={isEntitySharedWithClient}
                    lockedThreadId={kfa?.content?.contentThreadId}
                    mode="view"
                    projectId={projectId}
                    showSelected={!hideSelectedVersionToggleButtons}
                  />
                </div>
              )}

              <div className={styles['kfa-sub-header']}>
                <ActionsWithLock
                  buttonOnEdit={gotoEditHypothesis}
                  contentThreadId={commentingAutoLockContentId}
                  disableMoreMenu={disableMoreMenu}
                  hideEdit={hideEdit}
                  isEditDisabled={
                    disableEdit || viewClient.isOnClientVersion || isMobile
                  }
                  itemId={kfaContent?.contentId}
                  onDelete={() => setShowDeleteConfirmation(true)}
                  reportPreviewUrl={reportPreviewUrl}
                  viewClient={viewClient}
                  workflowActions={workflowActions}
                />

                <span>
                  {isMobile && visibleCommentCounts > 0 && (
                    <Badge
                      cssClass={analysisStyles.counter}
                      show
                      text={visibleCommentCountsText}
                    />
                  )}
                  <Button
                    className={styles['mobile-panel-button']}
                    aria-label="mobile-panel-button icon"
                    onClick={handlePanelButtonClick}
                    data-testid="panel-button"
                  >
                    {darkMode ? (
                      <Icon
                        name="tooltip-open-darkMode"
                        width="34"
                        height="32"
                      />
                    ) : (
                      <Icon
                        name="tooltip-open-lightMode"
                        width="34"
                        height="32"
                      />
                    )}
                  </Button>
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ViewHypothesisHeader;
